import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import api from "../../lib/api";
import { AdminHeader, Btn, Card, Input, Localized, Textarea } from "../../components/admin/Fields";
import ImageUpload from "../../components/admin/ImageUpload";
import VideoUpload from "../../components/admin/VideoUpload";

const emptySlide = () => ({
  kicker: { tr: "", en: "", ar: "" },
  title: { tr: "", en: "", ar: "" },
  subtitle: { tr: "", en: "", ar: "" },
  cta_label: { tr: "", en: "", ar: "" },
  cta_url: "",
  youtube_url: "",
  background_video: "",
  background_video_mobile: "",
  background_image: "",
  duration_ms: 10000,
});

const AdminHomepagePage = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/homepage").then((r) => setData(r.data || {})); }, []);

  const update = (path, value) =>
    setData((d) => {
      const clone = JSON.parse(JSON.stringify(d));
      const parts = path.split(".");
      let cur = clone;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = cur[parts[i]] || {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return clone;
    });

  const updateStat = (i, field, value) => {
    const stats = [...(data.stats || [])];
    if (field === "label") stats[i] = { ...stats[i], label: value };
    else stats[i] = { ...stats[i], [field]: value };
    setData({ ...data, stats });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/homepage", data);
      toast.success("Kaydedildi");
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };

  if (!data) return <div>…</div>;
  const hero = data.hero || {};
  const chairman = data.chairman || {};
  const slides = data.hero_slides || [];
  const setSlide = (i, patch) => setData((d) => ({ ...d, hero_slides: (d.hero_slides || []).map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const setSlideKey = (i, key, val) => setSlide(i, { [key]: val });
  const addSlide = () => setData((d) => ({ ...d, hero_slides: [...(d.hero_slides || []), emptySlide()] }));
  const removeSlide = (i) => setData((d) => ({ ...d, hero_slides: (d.hero_slides || []).filter((_, j) => j !== i) }));
  const moveSlide = (i, dir) => {
    setData((d) => {
      const arr = [...(d.hero_slides || [])];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...d, hero_slides: arr };
    });
  };

  return (
    <div data-testid="admin-homepage-page">
      <AdminHeader eyebrow="Homepage" title="Ana Sayfa Yönetimi" action={<Btn onClick={save} disabled={busy} testId="home-save">Kaydet</Btn>} />

      <div className="space-y-6">
        <Card title="Hero Slider (Ana Görsel Slaytlar)">
          <p className="text-sm text-brand-medium mb-6">
            Ana sayfanın üstünde dönen video/görsel slaytlar. Her slayt için desktop videoya (yatay), mobil videoya (dikey), poster görseline, başlık, açıklama ve buton ekleyebilirsiniz.
            YouTube URL'i girerseniz buton videoyu bir modal içinde açar; boş bırakırsanız buton verilen dahili URL'e gider.
          </p>
          <div className="space-y-6">
            {slides.map((sl, i) => (
              <div key={i} className="border border-black/10 bg-white p-4 md:p-6" data-testid={`slide-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-gold">Slayt 0{i + 1}</div>
                  <div className="flex gap-2">
                    <Btn variant="ghost" onClick={() => moveSlide(i, -1)}>↑</Btn>
                    <Btn variant="ghost" onClick={() => moveSlide(i, 1)}>↓</Btn>
                    <Btn variant="danger" onClick={() => removeSlide(i)}><Trash2 size={12} /></Btn>
                  </div>
                </div>
                <Localized label="Kicker (Başlık üstü)" value={sl.kicker} onChange={(v) => setSlideKey(i, "kicker", v)} />
                <div className="mt-4"><Localized label="Başlık" value={sl.title} onChange={(v) => setSlideKey(i, "title", v)} textarea /></div>
                <div className="mt-4"><Localized label="Alt Başlık" value={sl.subtitle} onChange={(v) => setSlideKey(i, "subtitle", v)} textarea /></div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Localized label="Buton Metni" value={sl.cta_label} onChange={(v) => setSlideKey(i, "cta_label", v)} />
                  <Input label="Buton URL (YouTube boşsa kullanılır)" value={sl.cta_url} onChange={(v) => setSlideKey(i, "cta_url", v)} testId={`slide-${i}-url`} />
                  <Input label="YouTube URL (buton modal açar)" value={sl.youtube_url} onChange={(v) => setSlideKey(i, "youtube_url", v)} testId={`slide-${i}-youtube`} />
                  <Input label="Otomatik Geçiş Süresi (ms)" type="number" value={sl.duration_ms} onChange={(v) => setSlideKey(i, "duration_ms", Number(v) || 10000)} />
                </div>
                <div className="mt-6 border-t border-black/10 pt-6">
                  <div className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-navy mb-1">Arka Plan Medyası</div>
                  <p className="text-xs text-brand-medium mb-4">
                    MP4 yükleyin veya URL yapıştırın — <b>YouTube linki de kabul edilir</b> ve otomatik olarak arka planda sessiz döngüde oynar. Video yoksa poster görsel gösterilir.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <VideoUpload label="Desktop (Yatay) Video" value={sl.background_video} onChange={(v) => setSlideKey(i, "background_video", v)} testId={`slide-${i}-video`} />
                    <VideoUpload label="Mobil (Dikey) Video" value={sl.background_video_mobile} onChange={(v) => setSlideKey(i, "background_video_mobile", v)} testId={`slide-${i}-video-mobile`} />
                    <ImageUpload label="Poster / Fallback Görsel" value={sl.background_image} onChange={(v) => setSlideKey(i, "background_image", v)} testId={`slide-${i}-poster`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <Btn onClick={addSlide} testId="add-slide"><Plus size={14} /> Yeni Slayt Ekle</Btn>
          </div>
        </Card>

        <Card title="Hakkımızda Metni">
          <Localized label="Başlık" value={data.about_title} onChange={(v) => update("about_title", v)} />
          <div className="mt-6"><Localized label="Metin" value={data.about_body} onChange={(v) => update("about_body", v)} textarea /></div>
        </Card>

        <Card title="İstatistikler">
          <div className="space-y-6">
            {(data.stats || []).map((s, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input label={`Değer #${i + 1}`} value={s.value} onChange={(v) => updateStat(i, "value", v)} />
                <div className="md:col-span-2"><Localized value={s.label} onChange={(v) => updateStat(i, "label", v)} /></div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Yönetim Kurulu Başkanı">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Ad" value={chairman.name} onChange={(v) => update("chairman.name", v)} />
            <Input label="Portre URL" value={chairman.portrait} onChange={(v) => update("chairman.portrait", v)} />
          </div>
          <div className="mt-6"><Localized label="Unvan" value={chairman.title} onChange={(v) => update("chairman.title", v)} /></div>
          <div className="mt-6"><Localized label="Mesaj" value={chairman.message} onChange={(v) => update("chairman.message", v)} textarea /></div>
        </Card>

        <Card title="Partnerler (JSON düzenleme)">
          <Textarea
            rows={8}
            value={JSON.stringify(data.partners || [], null, 2)}
            onChange={(v) => {
              try { const parsed = JSON.parse(v); update("partners", parsed); } catch { /* ignore invalid */ }
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default AdminHomepagePage;
